import React, { useEffect, useState } from 'react';
import { Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import { videoShortlistById } from '../../../api/endpoints';
import { PublicShareContainer } from '../../pro/Listing/components/PublicShareContainer';
import { DotLottleLoader } from '../../../components/Loader/DotLottleLoader';
import { ViewToggle } from '../../../components/ViewToggle/ViewToggle';
import { VideoCard } from '../../../components/videoSection/VideoCard';
import { UploadableVideoCard } from '../../../components/videoSection/VideoUpload';
import { useCommonParams } from '../../../hooks/useCommonParams';
import { DownloadDropdown } from '../../../components/DownloadDropdown';
import { DownloadVideoModal } from '../../../components/MenuOptions';
import { DownloadOverlay } from '../../../components/DownloadOverlay';
import { DownloadType } from '../../admin/videos/selectedVideoCardV2/helpers';
import VideoPlayerDialog from '../../../components/VideoPlayerDialog/VideoPlayerDialog';
import { formatVideoName } from '../../admin/videos/hooks/useFetchVideos';
import {
  collectZipKeys,
  buildZipSuffix,
  submitZip,
  isTimeoutError,
} from '../../../utils/downloadHelpers';
import { useDownloads } from '../../../components/Downloads';

export default function ShareVideoShortlist() {
  const { id } = useParams();
  const [shortlist, setShortlist] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { view: _view, setView } = useCommonParams({ defaultView: 'list' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const view = (isMobile ? 'grid' : _view) as 'grid' | 'list';

  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [showDownloadAllOptions, setShowDownloadAllOptions] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [zippingInProgress, setZippingInProgress] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');
  // Long-running zips are handed off to the global download tracker (top bar).
  const { promote } = useDownloads();

  useEffect(() => {
    if (!id) return;
    const fetchShortlist = async () => {
      try {
        const response = await axiosInstant.get(videoShortlistById(parseInt(id)));
        setShortlist(response.data);
      } catch (error) {
        console.error('Error fetching video shortlist:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchShortlist();
  }, [id]);

  const title = shortlist?.name || 'Video Shortlist';
  const videos = shortlist?.videos || [];

  const handleDownloadAll = async (type: DownloadType = DownloadType.WEBM) => {
    if (videos.length === 0) return;

    const baseName = formatVideoName(title || 'video-shortlist');
    const zipFileName = `${baseName}${buildZipSuffix(type)}.zip`;
    const roomId = Math.random().toString(36).substring(2, 15);

    try {
      setDownloadingAll(true);
      setZippingInProgress(true);
      setDownloadLink('');
      setDownloadModalOpen(false);

      const loadingId = toast.loading('Zipping files, please wait...');
      const { files, hasContent } = await collectZipKeys(videos, type);
      toast.dismiss(loadingId);

      if (!hasContent) {
        toast.error('No content available to download.');
        setZippingInProgress(false);
        setDownloadingAll(false);
        return;
      }

      const fileName = await submitZip(files, zipFileName, roomId);
      if (fileName) {
        setDownloadLink(fileName);
        setDownloadModalOpen(true);
      }
      setDownloadingAll(false);
      setZippingInProgress(false);
    } catch (error: any) {
      if (isTimeoutError(error)) {
        // Long-running zip: the global tracker's top bar takes over.
        promote(roomId, zipFileName);
      } else {
        toast.error(error.message ?? 'Download failed');
      }
      setDownloadingAll(false);
      setZippingInProgress(false);
    }
  };

  const descriptionContent = (
    <Typography variant="body2" color="darkgray1">
      This video shortlist contains a selection of videos grouped for easy review and sharing. Browse, preview, and download the videos below.
    </Typography>
  );

  if (loading) {
    return (
      <PublicShareContainer title="Loading...">
        <Box className="flex justify-center items-center py-20">
          <DotLottleLoader fill={false} />
        </Box>
      </PublicShareContainer>
    );
  }

  return (
    <>
      <DownloadOverlay visible={zippingInProgress} />

      <PublicShareContainer title={title} descriptionContent={descriptionContent}>
        <Box className="flex flex-col gap-6">
          {videos.length > 0 && (
            <>
              <Box className="flex flex-row justify-between items-center gap-3 flex-wrap">
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ fontSize: 32, '@media (max-width: 599.95px)': { fontSize: 22 } }}
                >
                  Videos
                </Typography>
                <Box className="flex items-center gap-3 justify-end">
                  <Box className="hidden sm:flex">
                    <ViewToggle
                      view={view}
                      onViewChange={(v) => setView(v as 'grid' | 'list')}
                      gridValue="grid"
                      listValue="list"
                    />
                  </Box>
                  {shortlist?.download_enabled && (
                    <DownloadDropdown
                      isOpen={showDownloadAllOptions}
                      isDownloading={downloadingAll || zippingInProgress}
                      onToggle={() => setShowDownloadAllOptions((v) => !v)}
                      onSelect={(key) => {
                        handleDownloadAll(key);
                        setShowDownloadAllOptions(false);
                      }}
                      onClickAway={() => setShowDownloadAllOptions(false)}
                    />
                  )}
                </Box>
              </Box>

              <Box className="flex gap-0 max-sm:items-center max-sm:gap-2">
                <Typography className="w-[110px] text-text-2 max-sm:w-auto max-sm:shrink-0 max-sm:text-sm">
                  {videos.length} {videos.length === 1 ? 'Video' : 'Videos'}
                </Typography>
                <div className="mt-3 border-t w-full border-border-token max-sm:mt-0" />
              </Box>
            </>
          )}

          {videos.length > 0 ? (
            <Box
              className={
                view === 'list'
                  ? 'flex flex-col gap-3'
                  : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              }
            >
              {videos.map((video: any) => (
                /* Drag-only, matching the pre-redesign card: its dropzone was gated on
                   role alone (isSharePage never touched the upload path), so a signed-in
                   admin had a drop target here and an anonymous visitor never did.
                   uploadSlot is not passed — the old control was invisible. */
                <UploadableVideoCard
                  key={video.id}
                  video={video}
                  project={(video as any).project}
                  refetch={() => undefined}
                >
                  {() => (
                    <VideoCard
                      video={video}
                      variant={view === 'list' ? 'row' : 'share'}
                      isShare
                      onOpen={(v) => setSelectedVideo(v)}
                      hideDownload={!shortlist?.download_enabled}
                      ignoreFileTypeSettings
                    />
                  )}
                </UploadableVideoCard>
              ))}
            </Box>
          ) : (
            <Box className="flex justify-center items-center py-12">
              <Typography variant="body1" color="textSecondary">
                No videos in this shortlist.
              </Typography>
            </Box>
          )}
        </Box>
      </PublicShareContainer>

      <DownloadVideoModal
        open={downloadModalOpen}
        onClose={() => {
          setDownloadModalOpen(false);
          setDownloadLink('');
        }}
        downloadSource={downloadLink}
      />

      <VideoPlayerDialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo}
      />
    </>
  );
}
